import React, { useEffect, useRef, useState } from "react";
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import { LinkItem } from "../LinkItem";
import { AnchorItem } from "./AnchorItem";
import { arrowLeft, arrowRight } from "../../../../utils/icons";
import { useTopBarState } from "../../../components/TopBar/TopBarContext";

type AnchorsProps = {
  links: Array<LinkItem>;
  background: string;
  hideOnMobile: boolean;
  sticky: boolean;
};

const Anchors: React.FunctionComponent<AnchorsProps> = ({
  links,
  background,
  hideOnMobile,
  sticky,
}): React.ReactElement => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSticky, setSticky] = useState(false);
  const [shouldRerender, setShouldRerender] = useState(false);
  const { visibleState: topBarVisibleState } = useTopBarState();

  const getTopOffset = (): number => {
    const topBarHeight = document.getElementById("main-top-bar")?.offsetHeight;
    const mediaQuery = window.matchMedia("(min-width: 990px)");
    const topBarOffset =
      topBarHeight && typeof topBarHeight === "number" ? topBarHeight : 0;
    const navbarOffset = mediaQuery.matches ? 50 : 0;

    return topBarOffset + navbarOffset;
  };

  const handleScroll = (): void => {
    const topOffset = getTopOffset();

    if (ref.current && sticky) {
      setSticky(ref.current.getBoundingClientRect().top <= topOffset);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", () => handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isSticky) {
      setShouldRerender(!shouldRerender);
    }
  }, [topBarVisibleState]);

  const LeftArrow = (): React.ReactElement => {
    const { isFirstItemVisible, scrollPrev } =
      React.useContext(VisibilityContext);

    return (
      <button
        aria-label="Scroll to previous anchors"
        className={`scroll-menu-arrow${
          isFirstItemVisible ? " scroll-menu-arrow--disabled" : ""
        }`}
        onClick={() => scrollPrev()}
      >
        {arrowLeft}
      </button>
    );
  };

  const RightArrow = (): React.ReactElement => {
    const { isLastItemVisible, scrollNext } =
      React.useContext(VisibilityContext);

    return (
      <button
        aria-label="Scroll to next anchors"
        className={`scroll-menu-arrow${
          isLastItemVisible ? " scroll-menu-arrow--disabled" : ""
        }`}
        onClick={() => scrollNext()}
      >
        {arrowRight}
      </button>
    );
  };

  return (
    <div ref={ref} className={hideOnMobile ? "anchors-hide-on-mobile" : ""}>
      <div
        style={{
          top: isSticky ? getTopOffset() : "initial",
        }}
        className={`${
          background === "grey"
            ? "anchors-container-background-grey"
            : "anchors-container-background-white"
        } ${isSticky ? "anchors-container-sticky" : ""}`}
      >
        <div className="desktop-container anchors-container">
          <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow}>
            {links.map((link) => (
              <AnchorItem
                link={link}
                itemId={link.anchor as string}
                key={link.anchor}
              />
            ))}
          </ScrollMenu>
        </div>
      </div>
    </div>
  );
};

export default Anchors;
